import React, { useRef } from 'react';
import Slider from 'react-slick';
import {
  Box,
  Typography,
  Container,
  Button,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Link } from 'react-router-dom';
import MapIcon from '@mui/icons-material/Map';
import InsightsIcon from '@mui/icons-material/Insights';

// Images in public/images/
const carouselImages = [
  'images/Mangrove.png',
  'images/Forest.png',
  'images/Cropland.png', 
  'images/Peatland.png', 
];

const services = [
  { label: 'Mangrove Coverage', icon: '🌴', link: '/monitoring?tab=mangrove' },
  { label: 'Forest Coverage', icon: '🌲', link: '/monitoring?tab=forest' },
  { label: 'Fire Alerts and Disturbance', icon: '🔥', link: '/monitoring?tab=fire' },
  { label: 'Weather Data', icon: '⛅', link: '/monitoring?tab=weather' },
  { label: 'Cropland Extend', icon: '🌾', link: '/monitoring?tab=cropland' },
  { label: 'Peatland Distribution', icon: '🌱', link: '/monitoring?tab=peatland' },
  { label: 'Risk Index', icon: '⚠️', link: '/monitoring?tab=risk' },
];

function chunkArray(array, chunkSize) {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
}

const serviceRows = chunkArray(services, 4);

const HeroCarousel = () => {
  const sliderRef = useRef(null);

  const carouselSettings = {
    dots: false,
    infinite: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    arrows: false,
  };

  return (
    <Box sx={{ position: 'relative', height: '100vh', width: '100vw', overflow: 'hidden' }}>
      <Slider ref={sliderRef} {...carouselSettings}>
        {carouselImages.map((src, i) => (
          <Box
            key={i}
            sx={{
              height: '100vh',
              width: '100vw',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#222',
            }}
          >
            <img
              src={src}
              alt={`carousel-${i}`}
              style={{
                width: '100%',
                height: '100vh',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block',
              }}
            />
            {/* Overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(83, 78, 78, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#fff',
                  fontWeight: 'bold',
                  fontFamily: 'Inter, sans-serif',
                  textAlign: 'center',
                  zIndex: 2,
                }}
              >
                Forest Monitoring and Beyond
              </Typography>
            </Box>
          </Box>
        ))}
      </Slider>
      {/* Carousel Arrows */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: 16,
          zIndex: 3,
          transform: 'translateY(-50%)',
        }}
      >
        <IconButton onClick={() => sliderRef.current.slickPrev()} sx={{ color: '#fff' }}>
          <ArrowBackIosNewIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: 16,
          zIndex: 3,
          transform: 'translateY(-50%)',
        }}
      >
        <IconButton onClick={() => sliderRef.current.slickNext()} sx={{ color: '#fff' }}>
          <ArrowForwardIosIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

// About Us card content
const aboutCards = [
  {
    title: 'Commitment to the',
    highlight: 'Environment',
    text: 'We aim to create a sustainable future by leveraging technology for environmenal monitoring through remote sensing.',
  },
  {
    title: 'Focused on',
    highlight: 'Sarawak',
    text: 'We aim to provide a solution with a focus on Sarawak, Malaysia, to address local environmental challenges.',
  },
  {
    title: 'Supporting',
    highlight: 'Communities',
    text: 'Our mission includes empowering local communities to sustainably manage forests.',
  },
];

const AboutUsSection = () => {
  const sliderRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const settings = {
    dots: true,
    arrows: false, // External arrows are used for non-mobile
    infinite: true,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    swipeToSlide: true,
    centerMode: false, // Explicitly false to prevent peeking
    variableWidth: false, // Explicitly false for simplicity with slidesToShow: 1

    dotsClass: "slick-dots custom-dots", // Custom class for dots
    appendDots: dots => (
      <div style={{
        position: "absolute",
        bottom: "-35px", 
        width: "100%",
        textAlign: "center", // Center the ul
      }}>
        <ul style={{ margin: "0", padding: "0", display: 'inline-block' }}> {dots} </ul>
      </div>
    ),
    customPaging: i => (
      <div
        style={{
          width: "10px", // Dot size
          height: "10px",
          margin: "0 4px", // Space between dots
          backgroundColor: "rgba(163, 197, 125, 0.5)", // Inactive dot color
          borderRadius: "50%",
          display: 'inline-block',
          cursor: 'pointer',
        }}
        className="custom-dot-paging" // Add class for active state styling
      />
    ),
  };

  const cardOuterBoxSx = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'stretch',
    width: '100%',
    px: { xs: 2, sm: 0 }, // Add horizontal padding on mobile, same as services section
  };

  return (
     <Box sx={{ bgcolor: '#fff9ed', py: { xs: 5, md: 8 }, pb: { xs: 8, md: 10 } }}> {/* Ensure enough padding for dots */}
      <Container maxWidth="md">
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 6 }}>
          ABOUT US
        </Typography>

        {/* This Box is the main container for the slider and its arrows */}
        <Box sx={{ 
          position: 'relative',
          mx: "auto", // Center the slider container
          // Control the width of the slider area.
          width: { xs: '90%', sm: '85%', md: '75%', lg: '70%' }, 
          mb: 6 // Increased margin for dots
        }}>
          <style jsx global>{`
            .slick-dots.custom-dots li button:before {
              content: none;
            }
            .slick-dots.custom-dots li.slick-active .custom-dot-paging {
              background-color: #a3c57d;
              box-shadow: 0 0 0 2px #71a865ff;
              transform: scale(1.2);
              transition: all 0.2s;
            }
          `}</style>
          <Slider ref={sliderRef} {...settings}>
            {aboutCards.map((card, index) => (
              // Each child of Slider is a slide.
              // We use padding on this Box if slides need space around the card.
              <Box 
                key={index} 
                sx={{ 
                  outline: 'none', // Remove outline on focus for the slide wrapper
                  // Add padding here to increase the space *around* the card within the slide area
                }}
              >
                {/* This inner Box centers the Card within the slide */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch', width: '100%' }}>
                  <Card
                    sx={{
                      backgroundColor: '#a3c57d',
                      borderRadius: { xs: '16px', md: '20px' },
                      width: '100%',
                      maxWidth: 400, 
                      minWidth: 280, 
                      minHeight: { xs: 130, sm: 150, md: 170 },
                      p: { xs: 2.5, md: 3.5 },
                      textAlign: 'center',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                  >
                    <CardContent sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography 
                        variant="h6" 
                        fontWeight={600}
                        sx={{ fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }, mb: 1 }}
                      >
                        {card.title}{' '}
                        <Typography
                          component="span"
                          sx={{ color: '#d26500', fontWeight: 'bold', fontSize: 'inherit' }}
                        >
                          {card.highlight}
                        </Typography>
                      </Typography>
                      <Typography 
                        sx={{ 
                          mt: 1, 
                          mb: 0, // Text is last element, no bottom margin needed here
                          fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
                          px: { xs: 0.5, md: 1 },
                          lineHeight: 1.5,
                        }}
                      >
                        {card.text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            ))}
          </Slider>
        </Box>
        
        {isMobile && (
          <Typography 
            variant="caption" 
            sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 0, // Adjusted as slider container has more mb
              color: 'text.secondary',
              fontStyle: 'italic',
              fontSize: '0.8rem'
            }}
          >
            Swipe to see more
          </Typography>
        )}
      </Container>
    </Box>
  );
};

const HomePage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <>
      <HeroCarousel />
      <AboutUsSection />
  
      {/* Services Section */}
      <Box
        sx={{
          backgroundColor: '#fcf9f3',
          py: 10,
          textAlign: 'center',
          px: { xs: 2, sm: 0 }, 
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 6 }}>
          SERVICES DESIGNED FOR YOUR EVERY NEED
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: 6,
            maxWidth: 900,
            mx: 'auto',
            width: '100%',
          }}
        >
          {/* Monitoring Map Card */}
          <Card
            sx={{
              flex: 1,
              minWidth: 280,
              maxWidth: 400,
              p: 4,
              borderRadius: 4,
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              backgroundColor: '#a3c57d',
              mb: isMobile ? 4 : 0,
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <MapIcon sx={{ fontSize: 48, color: '#d26500' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Monitoring Map
              </Typography>
              <Typography sx={{ mb: 3 }}>
                Explore interactive maps of Sarawak's mangroves, forests, croplands, peatlands, and fire alerts. Visualise environmental changes, toggle layers, and access up-to-date remote sensing data.
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/monitoring"
                sx={{ mt: 2, alignSelf: 'center' }}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>

          {/* Analysis Card */}
          <Card
            sx={{
              flex: 1,
              minWidth: 280,
              maxWidth: 400,
              p: 4,
              borderRadius: 4,
              boxShadow: 3,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              backgroundColor: '#a3c57d',
            }}
          >
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                <InsightsIcon sx={{ fontSize: 48, color: '#d26500' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Analysis
              </Typography>
              <Typography sx={{ mb: 3 }}>
                Dive into analytics dashboards for fire trends, forest cover change, climate correlations, and regional comparisons. Gain insights with interactive charts and data-driven reports.
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/analysis"
                sx={{ mt: 2, alignSelf: 'center' }}
              >
                Learn More
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
} 

export default HomePage;